/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

// ─────────────────────────────────────────────────────────────────────────────
// Backend API base URL. Always points directly at the local backend —
// never at an ngrok URL. ngrok is only used to share the React app itself
// with the team; the app always talks to the backend at localhost:4000
// directly, from the browser running on your machine.
// ─────────────────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — "control tower" palette.
// Warm paper background instead of clinical white/gray. Ink for structure,
// amber for "needs attention," teal for "in motion," green/red for resolved
// and critical. Mono face for IDs and timestamps — this is operational data,
// it should look like data, not like marketing copy.
// ─────────────────────────────────────────────────────────────────────────────
// ── FLUID BRAND TOKENS ───────────────────────────────────────────────────────
// Extracted from Fluid by Pickyourtrail brand guide:
//   Primary purple: #3D2DB5 (deep brand purple, buttons, active states)
//   Lavender bg:    #EEE9FF (page background)
//   Light purple:   #E8E3FF (secondary backgrounds, hover states)
//   White card:     #FFFFFF (card surfaces)
//   Border:         #D8D2F8 (purple-tinted borders)
//   Text ink:       #1A1040 (near-black with purple undertone)
//   Text muted:     #6B63A0 (secondary text)
//   Amber/warning:  #E8580C (unchanged — universal warning color)
//   Green/success:  #1F7A40 (unchanged)
//   Red/error:      #C92A2A (unchanged)

const T = {
  // Brand
  brand:        "#3D2DB5",   // primary purple — buttons, active states, logo
  brandDark:    "#2D1B8E",   // darker purple — hover, pressed
  brandLight:   "#5B4FD4",   // lighter purple — secondary actions
  brandBg:      "#EEE9FF",   // lavender — page background
  brandBgLight: "#F5F2FF",   // very light purple — card hover
  brandSurface: "#E8E3FF",   // medium lavender — chips, tags, secondary bg

  // Text
  ink:          "#1A1040",   // near-black with purple undertone
  inkSoft:      "#3D3570",   // secondary text
  muted:        "#6B63A0",   // muted text
  mutedSoft:    "#A099C8",   // very muted

  // Surfaces
  paper:        "#F5F2FF",   // page background (slight purple tint)
  paperRaised:  "#FFFFFF",   // card surface (pure white)
  sand:         "#EEE9FF",   // subtle background areas

  // Borders
  line:         "#E0D9F5",   // default border (purple-tinted)
  lineStrong:   "#D0C8EE",   // stronger border

  // Semantic — kept from original for status badges
  amber:        "#D4700A",
  amberBg:      "#FEF0E0",
  amberLine:    "#E8A04A",
  teal:         "#0B7285",
  tealBg:       "#E1F1F3",
  tealLine:     "#0B7285",
  green:        "#1F7A40",
  greenBg:      "#E8F5ED",
  greenLine:    "#2B8A3E",
  red:          "#C92A2A",
  redBg:        "#FCE9E9",
  redLine:      "#C92A2A",
  purple:       "#3D2DB5",   // maps to brand
  purpleBg:     "#EEE9FF",
  purpleLine:   "#3D2DB5",
};

const FONT_SANS = `-apple-system, "Inter", "Helvetica Neue", Arial, sans-serif`;
const FONT_MONO = `"JetBrains Mono", "SF Mono", "IBM Plex Mono", Menlo, Consolas, monospace`;

const SEVERITY = {
  critical: { label: "Critical",        bg: T.redBg,    line: T.redLine,    text: T.red    },
  high:     { label: "High",            bg: T.amberBg,  line: T.amberLine,  text: T.amber  },
  medium:   { label: "Medium",          bg: T.sand,     line: T.lineStrong, text: T.inkSoft },
  low:       { label: "Low",            bg: T.tealBg,   line: T.tealLine,   text: T.teal   },
  feature:  { label: "Feature request", bg: T.purpleBg, line: T.purpleLine, text: T.purple },
};

const IMPACT = {
  customer:   { label: "Customer impact",   icon: "ti-users" },
  operations: { label: "Operations impact", icon: "ti-settings-2" },
  both:       { label: "Customer & ops",    icon: "ti-arrows-join" },
};

const CHANNELS = ["#fluid-prodbugs", "#fluid-flight-errors", "#fluid-hotel-errors", "#fluid-bus-errors"];
const MODULES  = ["Flights", "Hotels", "Cabs", "Buses", "Trains", "Platform"];
const STATUSES = ["New", "In Review", "In Progress", "UAT", "Resolved"];

const STATUS_STYLE = {
  "New":         { bg: T.sand,    line: T.lineStrong, text: T.muted },
  "In Review":   { bg: T.amberBg, line: T.amberLine,  text: T.amber },
  "In Progress": { bg: T.tealBg,  line: T.tealLine,   text: T.teal  },
  "UAT":         { bg: T.purpleBg,line: T.purpleLine, text: T.purple },
  "Resolved":    { bg: T.greenBg, line: T.greenLine,  text: T.green },
  "Closed":      { bg: T.greenBg, line: T.greenLine,  text: T.green },
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Reusable pieces ───────────────────────────────────────────────────────────

function Badge({ label, bg, line, text, mono = false }) {
  return (
    <span style={{
      fontSize: 11, padding: "3px 9px", borderRadius: 4,
      backgroundColor: bg, color: text, border: `1px solid ${line}`,
      fontWeight: 600, letterSpacing: 0.2, whiteSpace: "nowrap", display: "inline-block",
      fontFamily: mono ? FONT_MONO : FONT_SANS,
    }}>
      {label}
    </span>
  );
}

function PulseDot({ color = T.teal }) {
  return (
    <span style={{ position: "relative", display: "inline-flex", width: 8, height: 8 }}>
      <span style={{
        position: "absolute", inset: 0, borderRadius: "50%", backgroundColor: color,
        opacity: 0.45, animation: "fluid-pulse 2s ease-out infinite",
      }} />
      <span style={{
        position: "relative", width: 8, height: 8, borderRadius: "50%", backgroundColor: color,
      }} />
    </span>
  );
}

function Tab({ label, active, onClick, count }) {
  return (
    <button onClick={onClick} style={{
      background: "none", border: "none", position: "relative",
      padding: "11px 4px", marginRight: 22, cursor: "pointer",
      fontSize: 13.5, fontFamily: FONT_SANS,
      fontWeight: active ? 700 : 500,
      color: active ? T.brand : T.muted,
      display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap",
      transition: "color 0.15s ease",
    }}>
      {label}
      {count !== undefined && (
        <span style={{
          backgroundColor: active ? T.brand : T.brandBg,
          color: active ? T.paper : T.muted,
          borderRadius: 10, fontSize: 10.5, fontWeight: 700,
          padding: "1px 6px", fontFamily: FONT_MONO, minWidth: 18, textAlign: "center",
        }}>
          {count}
        </span>
      )}
      {active && (
        <span style={{
          position: "absolute", bottom: -1, left: 0, right: 0, height: 2.5,
          backgroundColor: T.brand, borderRadius: 2,
        }} />
      )}
    </button>
  );
}

function IconButton({ icon, label, onClick, variant = "default" }) {
  const styles = {
    default:   { bg: T.paperRaised, line: T.line,    text: T.inkSoft },
    primary:   { bg: T.brand,       line: T.brand,   text: T.paperRaised },
    ghost:     { bg: "transparent", line: "transparent", text: T.muted },
  }[variant];
  return (
    <button onClick={onClick} style={{
      fontSize: 12.5, padding: "6px 12px", borderRadius: 7,
      border: `1px solid ${styles.line}`, backgroundColor: styles.bg,
      cursor: "pointer", color: styles.text, fontFamily: FONT_SANS,
      fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6,
      transition: "all 0.12s ease", whiteSpace: "nowrap",
    }}>
      {icon && <i className={`ti ${icon}`} style={{ fontSize: 14 }} />}
      {label}
    </button>
  );
}

// ── Issue card ─────────────────────────────────────────────────────────────────

function IssueCard({ issue, onValidate, onView, onDelete }) {
  const sev = issue.severity ? SEVERITY[issue.severity] : null;
  const accentColor = issue.tracking ? T.teal : issue.severity === "critical" ? T.red : T.lineStrong;

  return (
    <div style={{
      backgroundColor: T.paperRaised,
      border: `1px solid ${T.line}`,
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: 10, padding: "15px 18px",
      display: "flex", flexDirection: "column", gap: 9,
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: T.mutedSoft, fontFamily: FONT_MONO, letterSpacing: 0.3,
          }}>
            #{issue.id.slice(-6)}
          </span>
          <Badge
            label={issue.type === "bug" ? "Bug" : "Feature"}
            bg={issue.type === "bug" ? T.redBg : T.purpleBg}
            line={issue.type === "bug" ? T.redLine : T.purpleLine}
            text={issue.type === "bug" ? T.red : T.purple}
          />
          <Badge label={issue.module || "Platform"} bg={T.sand} line={T.lineStrong} text={T.inkSoft} />
          {sev && <Badge label={sev.label} bg={sev.bg} line={sev.line} text={sev.text} />}
          {issue.tracking && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11, fontWeight: 700, color: T.teal, padding: "3px 9px",
              borderRadius: 4, backgroundColor: T.tealBg, border: `1px solid ${T.tealLine}` }}>
              <PulseDot color={T.teal} />Tracked
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {!issue.tracking && (
            <button onClick={() => onValidate(issue)} style={{
              fontSize: 12.5, padding: "6px 14px", borderRadius: 7,
              border: `1px solid ${T.brand}`, backgroundColor: T.brand,
              cursor: "pointer", color: T.paperRaised, fontFamily: FONT_SANS,
              fontWeight: 600, whiteSpace: "nowrap",
            }}>Validate</button>
          )}
          <button onClick={() => onView(issue)} style={{
            fontSize: 12.5, padding: "6px 14px", borderRadius: 7,
            border: `1px solid ${T.line}`, backgroundColor: T.paperRaised,
            cursor: "pointer", color: T.inkSoft, fontFamily: FONT_SANS,
            fontWeight: 600, whiteSpace: "nowrap",
          }}>View</button>
          <button onClick={() => onDelete(issue)} title="Delete issue"
            style={{
              fontSize: 12.5, padding: "6px 10px", borderRadius: 7,
              border: `1px solid ${T.redLine}`, backgroundColor: T.redBg,
              cursor: "pointer", color: T.red, fontFamily: FONT_SANS,
              fontWeight: 600, whiteSpace: "nowrap",
            }}>Delete</button>
        </div>
      </div>

      <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: T.ink, lineHeight: 1.45, fontFamily: FONT_SANS }}>
        {issue.title}
      </p>

      {/* Screenshot thumbnail strip + Slack link ── lazy-loaded only for tasks
          that Asana reports have attachments, keeping the card list fast */}
      {(issue.hasAttachments || issue.slackUrl) && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
          {issue.hasAttachments && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11.5, color: T.teal, fontWeight: 600,
              backgroundColor: T.tealBg, padding: "3px 9px", borderRadius: 4,
              border: `1px solid ${T.tealLine}` }}>
              <i className="ti ti-camera" style={{ fontSize: 13 }} />
              Screenshot attached
            </span>
          )}
          {issue.slackUrl && (
            <a href={issue.slackUrl} target="_blank" rel="noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: 11.5, color: T.amber, fontWeight: 600,
                backgroundColor: T.amberBg, padding: "3px 9px", borderRadius: 4,
                border: `1px solid ${T.amberLine}`, textDecoration: "none" }}>
              <i className="ti ti-brand-slack" style={{ fontSize: 13 }} />
              View in Slack
            </a>
          )}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginTop: 1 }}>
        <span style={{ fontSize: 12, color: T.muted, display: "flex", alignItems: "center", gap: 4, fontFamily: FONT_SANS }}>
          <i className="ti ti-message-2" style={{ fontSize: 13 }} />{issue.channel}
        </span>
        <span style={{ fontSize: 12, color: T.muted, display: "flex", alignItems: "center", gap: 4 }}>
          <i className="ti ti-user" style={{ fontSize: 13 }} />{issue.reporter}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <i className="ti ti-clock" style={{ fontSize: 13, color: T.mutedSoft }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: T.inkSoft, fontFamily: FONT_MONO }}>
            {formatDate(issue.reportedAt)}
          </span>
          <span style={{ fontSize: 11, color: T.mutedSoft, fontFamily: FONT_SANS }}>
            ({timeAgo(issue.reportedAt)})
          </span>
        </span>
        {issue.status !== "New" && (
          <Badge
            label={issue.status}
            bg={STATUS_STYLE[issue.status]?.bg || T.sand}
            line={STATUS_STYLE[issue.status]?.line || T.line}
            text={STATUS_STYLE[issue.status]?.text || T.muted}
          />
        )}
      </div>
    </div>
  );
}

// ── Validate modal ────────────────────────────────────────────────────────────

function ValidateModal({ issue, onClose, onSave, saving }) {
  const [severity, setSeverity]   = useState(issue.severity  || "");
  const [impact, setImpact]       = useState(issue.impact    || "");
  const [issueType, setIssueType] = useState(issue.issueType || "");
  const [note, setNote]           = useState(issue.smeNote   || "");
  const [smeName, setSmeName]     = useState(issue.smeName   || "");
  const [markTracking, setMarkTracking] = useState(false);
  const isNotABug  = severity === 'not_a_bug';
  const isResolved = severity === 'resolved';
  const isShortcut = isNotABug || isResolved;
  const canSave = severity && smeName && (isShortcut || (impact && issueType));

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const inputStyle = {
    width: "100%", fontSize: 13.5, padding: "9px 11px", borderRadius: 7,
    border: `1px solid ${T.line}`, backgroundColor: T.paperRaised, color: T.ink,
    boxSizing: "border-box", fontFamily: FONT_SANS, outline: "none",
  };
  const labelStyle = { fontSize: 12, color: T.inkSoft, display: "block", marginBottom: 6, fontWeight: 700, letterSpacing: 0.1 };

  const modalContent = (
    <div
      onClick={onClose}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        width: "100vw", height: "100vh",
        backgroundColor: "rgba(19, 21, 26, 0.6)",
        WebkitBackdropFilter: "blur(3px)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 2147483000, padding: 16,
        WebkitTransform: "translateZ(0)", transform: "translateZ(0)",
        isolation: "isolate",
      }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: T.paperRaised, borderRadius: 16,
          border: `1px solid ${T.line}`, width: "100%", maxWidth: 560,
          maxHeight: "88vh", overflowY: "auto",
          boxShadow: "0 24px 60px rgba(19,21,26,0.4)",
          fontFamily: FONT_SANS,
        }}>
        <div style={{
          padding: "20px 24px 16px", borderBottom: `1px solid ${T.line}`,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          backgroundColor: T.paperRaised, position: "sticky", top: 0, zIndex: 1,
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: T.amber, letterSpacing: 0.6, textTransform: "uppercase" }}>
              SME validation
            </p>
            <p style={{ margin: "4px 0 0", fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.4 }}>
              {issue.title.slice(0, 60)}{issue.title.length > 60 ? "…" : ""}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: T.sand, border: "none", cursor: "pointer", fontSize: 15,
            color: T.inkSoft, width: 30, height: 30, borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 12,
          }}>
            <i className="ti ti-x" />
          </button>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{
            backgroundColor: T.paper, borderRadius: 10, padding: "12px 14px",
            fontSize: 13, color: T.inkSoft, lineHeight: 1.65, borderLeft: `3px solid ${T.lineStrong}`,
          }}>
            <p style={{ margin: "0 0 5px", fontWeight: 700, fontSize: 10.5, color: T.mutedSoft,
              textTransform: "uppercase", letterSpacing: 0.6 }}>Original Slack message</p>
            {issue.rawMessage || issue.title}
          </div>

          <div>
            <label style={labelStyle}>Severity</label>
            <select value={severity} onChange={e => {
              const v = e.target.value;
              setSeverity(v);
              if (v === 'not_a_bug') { setImpact('not_a_bug'); setIssueType(''); setMarkTracking(false); }
              if (v === 'resolved')  { setImpact('resolved');  setIssueType(''); setMarkTracking(false); }
            }} style={{ ...inputStyle, borderColor: isNotABug ? T.green : isResolved ? T.tealLine : T.line }}>
              <option value="">Select severity</option>
              {Object.entries(SEVERITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              <option value="resolved">Resolved</option>
              <option value="not_a_bug">Not a bug</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Impact area</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {Object.entries(IMPACT).map(([k, v]) => (
                <button key={k} onClick={() => setImpact(k)} disabled={isShortcut} style={{
                  flex: 1, minWidth: 90, padding: "10px 6px", borderRadius: 9,
                  border: `1.5px solid ${impact === k ? T.teal : T.line}`,
                  backgroundColor: impact === k ? T.tealBg : T.paper,
                  cursor: isShortcut ? "not-allowed" : "pointer", fontSize: 12, fontFamily: FONT_SANS,
                  color: impact === k ? T.teal : T.muted,
                  fontWeight: impact === k ? 700 : 500, textAlign: "center",
                  opacity: isShortcut ? 0.4 : 1, transition: "all 0.12s ease",
                }}>
                  <i className={`ti ${v.icon}`} style={{ display: "block", fontSize: 17, marginBottom: 4 }} />
                  {v.label}
                </button>
              ))}
              <button onClick={e => { e.stopPropagation(); setImpact('not_a_bug'); setSeverity('not_a_bug'); setPriority(''); setMarkTracking(false); }} style={{
                flex: 1, minWidth: 90, padding: "10px 6px", borderRadius: 9,
                border: `1.5px solid ${impact === 'not_a_bug' ? T.green : T.line}`,
                backgroundColor: impact === 'not_a_bug' ? T.greenBg : T.paper,
                cursor: "pointer", fontSize: 12, fontFamily: FONT_SANS,
                color: impact === 'not_a_bug' ? T.green : T.muted,
                fontWeight: impact === 'not_a_bug' ? 700 : 500, textAlign: "center",
                transition: "all 0.12s ease",
              }}>
                <i className="ti ti-circle-x" style={{ display: "block", fontSize: 17, marginBottom: 4 }} />
                Not a bug
              </button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Issue type</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { key: "new",         label: "New",               color: T.brand,  bg: T.purpleBg, line: T.purpleLine },
                { key: "recurring",   label: "Recurring",         color: T.red,    bg: T.redBg,    line: T.redLine   },
                { key: "resurfacing", label: "Resurfacing Again", color: T.amber,  bg: T.amberBg,  line: T.amberLine },
              ].map(opt => (
                <button key={opt.key}
                  onClick={() => setIssueType(issueType === opt.key ? "" : opt.key)}
                  disabled={isShortcut}
                  style={{
                    flex: 1, padding: "10px 8px", borderRadius: 9, cursor: isShortcut ? "not-allowed" : "pointer",
                    border: `1.5px solid ${issueType === opt.key ? opt.line : T.line}`,
                    backgroundColor: issueType === opt.key ? opt.bg : T.paper,
                    color: issueType === opt.key ? opt.color : T.muted,
                    fontSize: 12.5, fontWeight: issueType === opt.key ? 700 : 500,
                    fontFamily: FONT_SANS, textAlign: "center",
                    opacity: isShortcut ? 0.4 : 1, transition: "all 0.12s ease",
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle}>SME name</label>
            <select value={smeName} onChange={e => setSmeName(e.target.value)} style={inputStyle}>
              <option value="">Select your name</option>
              <option value="Govarthan M">Govarthan M</option>
              <option value="Shameer S">Shameer S</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Validation notes</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder="Reproduction steps, business impact, or additional context…"
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }} />
          </div>

          {isNotABug ? (
            <div style={{ backgroundColor: T.greenBg, borderRadius: 10, padding: "13px 14px",
              border: `1.5px solid ${T.greenLine}`, display: "flex", gap: 10, alignItems: "center" }}>
              <i className="ti ti-circle-x" style={{ fontSize: 18, color: T.green, flexShrink: 0 }} />
              <div>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: T.green }}>Marked as not a bug</p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: T.green, lineHeight: 1.5, opacity: 0.8 }}>
                  This issue will be closed and will not be tracked with the product team.
                </p>
              </div>
            </div>
          ) : isResolved ? (
            <div style={{ backgroundColor: T.tealBg, borderRadius: 10, padding: "13px 14px",
              border: `1.5px solid ${T.tealLine}`, display: "flex", gap: 10, alignItems: "center" }}>
              <i className="ti ti-circle-check" style={{ fontSize: 18, color: T.teal, flexShrink: 0 }} />
              <div>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: T.teal }}>Marked as resolved</p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: T.teal, lineHeight: 1.5, opacity: 0.8 }}>
                  Fix already deployed by product support. This will be saved and closed without tracking.
                </p>
              </div>
            </div>
          ) : (
            <label style={{
              display: "flex", alignItems: "flex-start", gap: 11, cursor: "pointer", padding: "13px 14px",
              borderRadius: 10, border: `1.5px solid ${markTracking ? T.teal : T.line}`,
              backgroundColor: markTracking ? T.tealBg : T.paper, transition: "all 0.12s ease",
            }}>
              <input type="checkbox" checked={markTracking} onChange={e => setMarkTracking(e.target.checked)}
                style={{ width: 17, height: 17, flexShrink: 0, marginTop: 1, accentColor: T.teal }} />
              <div>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: markTracking ? T.teal : T.ink }}>
                  Mark for active tracking with product team
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
                  Syncs this issue to the shared Google Sheet automatically
                </p>
              </div>
            </label>
          )}

          {markTracking && (
            <div style={{
              backgroundColor: T.ink, borderRadius: 9, padding: "11px 14px", fontSize: 12.5,
              color: T.paper, display: "flex", gap: 9, alignItems: "flex-start", lineHeight: 1.5,
            }}>
              <i className="ti ti-table-export" style={{ flexShrink: 0, marginTop: 1, color: T.amber }} />
              <span>A new row will be written to <strong>Fluid Platform Tracker</strong> with every field pre-filled.</span>
            </div>
          )}
        </div>

        <div style={{
          padding: "16px 24px", borderTop: `1px solid ${T.line}`,
          display: "flex", justifyContent: "flex-end", gap: 10,
          backgroundColor: T.paperRaised, position: "sticky", bottom: 0,
        }}>
          <button onClick={onClose} style={{
            padding: "9px 18px", borderRadius: 8, border: `1px solid ${T.line}`,
            backgroundColor: T.paperRaised, cursor: "pointer", fontSize: 13,
            color: T.muted, fontWeight: 600, fontFamily: FONT_SANS,
          }}>
            Cancel
          </button>
          <button
            onClick={() => canSave && !saving && onSave({ severity, impact, issueType, smeNote: note, smeName, tracking: markTracking })}
            style={{
              padding: "9px 20px", borderRadius: 8, border: "none",
              backgroundColor: canSave ? T.brand : T.line,
              color: canSave ? T.paper : T.mutedSoft,
              cursor: canSave && !saving ? "pointer" : "default", fontSize: 13,
              fontWeight: 700, fontFamily: FONT_SANS,
              display: "inline-flex", alignItems: "center", gap: 7,
            }}>
            {saving
              ? <><i className="ti ti-loader-2" style={{ animation: "fluid-spin 0.8s linear infinite" }} />Saving…</>
              : isShortcut ? "Save validation"
              : markTracking ? <>Validate & sync to sheet<i className="ti ti-arrow-up-right" /></>
              : "Save validation"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// ── View modal ─────────────────────────────────────────────────────────────────

function ViewModal({ issue, onClose, onStatusChange }) {
  const sev = issue.severity ? SEVERITY[issue.severity] : null;
  const [attachments, setAttachments] = useState(issue.attachments || []);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Fetch attachments lazily when the modal opens — only if not already loaded.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (attachments.length > 0) return; // already have them — don't re-fetch
    const gid = issue.asanaTaskGid || issue.id;
    if (!gid) return;
    setLoadingAttachments(true);
    fetch(`${API}/api/task/${gid}/attachments`)
      .then(r => r.json())
      .then(data => { if (data.attachments?.length) setAttachments(data.attachments); })
      .catch(() => {})
      .finally(() => setLoadingAttachments(false));
  // issue.id is the only dep we want — adding attachments.length would cause
  // an infinite re-fetch loop since fetching updates attachments.
  }, [issue.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const modalContent = (
    <div
      onClick={onClose}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        width: "100vw", height: "100vh",
        backgroundColor: "rgba(19, 21, 26, 0.6)",
        WebkitBackdropFilter: "blur(3px)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 2147483000, padding: 16,
        WebkitTransform: "translateZ(0)", transform: "translateZ(0)",
        isolation: "isolate",
      }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: T.paperRaised, borderRadius: 16,
          border: `1px solid ${T.line}`, width: "100%", maxWidth: 600,
          maxHeight: "88vh", overflowY: "auto",
          boxShadow: "0 24px 60px rgba(19,21,26,0.4)",
          fontFamily: FONT_SANS,
        }}>
        <div style={{
          padding: "20px 24px 16px", borderBottom: `1px solid ${T.line}`,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          backgroundColor: T.paperRaised, position: "sticky", top: 0,
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Badge
                label={issue.type === "bug" ? "Bug" : "Feature"}
                bg={issue.type === "bug" ? T.redBg : T.purpleBg}
                line={issue.type === "bug" ? T.redLine : T.purpleLine}
                text={issue.type === "bug" ? T.red : T.purple}
              />
              <Badge label={issue.module} bg={T.sand} line={T.lineStrong} text={T.inkSoft} />
              {sev && <Badge label={sev.label} bg={sev.bg} line={sev.line} text={sev.text} />}
              {issue.tracking && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
                  fontSize: 11, fontWeight: 700, color: T.teal, padding: "3px 9px",
                  borderRadius: 4, backgroundColor: T.tealBg, border: `1px solid ${T.tealLine}` }}>
                  <PulseDot color={T.teal} />Tracked
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: T.ink, lineHeight: 1.4 }}>{issue.title}</p>
          </div>
          <button onClick={onClose} style={{
            background: T.sand, border: "none", cursor: "pointer", fontSize: 15,
            color: T.inkSoft, width: 30, height: 30, borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 12,
          }}>
            <i className="ti ti-x" />
          </button>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {[["Channel", issue.channel], ["Reporter", issue.reporter], ["Reported", formatDate(issue.reportedAt)]].map(([k, v]) => (
              <div key={k} style={{ backgroundColor: T.paper, borderRadius: 9, padding: "10px 12px" }}>
                <p style={{ margin: 0, fontSize: 10.5, color: T.mutedSoft, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: 0.5 }}>{k}</p>
                <p style={{ margin: "3px 0 0", fontSize: 13, fontWeight: 700, color: T.ink }}>{v}</p>
              </div>
            ))}
          </div>

          {/* Slack message deep link */}
          {issue.slackUrl && (
            <a href={issue.slackUrl} target="_blank" rel="noreferrer"
              style={{
                display: "flex", alignItems: "center", gap: 10,
                backgroundColor: T.amberBg, border: `1px solid ${T.amberLine}`,
                borderRadius: 9, padding: "10px 14px", textDecoration: "none",
              }}>
              <i className="ti ti-brand-slack" style={{ fontSize: 18, color: T.amber, flexShrink: 0 }} />
              <div>
                <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: T.amber }}>
                  View original Slack message
                </p>
                <p style={{ margin: "1px 0 0", fontSize: 11.5, color: T.amber, opacity: 0.75,
                  fontFamily: FONT_MONO, wordBreak: "break-all" }}>
                  {issue.slackUrl}
                </p>
              </div>
              <i className="ti ti-external-link" style={{ fontSize: 14, color: T.amber, marginLeft: "auto", flexShrink: 0 }} />
            </a>
          )}

          <div style={{ backgroundColor: T.paper, borderRadius: 10, padding: "12px 14px", borderLeft: `3px solid ${T.lineStrong}` }}>
            <p style={{ margin: "0 0 5px", fontSize: 10.5, color: T.mutedSoft, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: 0.6 }}>Original Slack message</p>
            <p style={{ margin: 0, fontSize: 13, color: T.inkSoft, lineHeight: 1.7 }}>
              {issue.rawMessage || issue.title}
            </p>
          </div>

          {loadingAttachments && (
            <div style={{ fontSize: 12, color: T.muted, display: "flex", alignItems: "center", gap: 6 }}>
              <i className="ti ti-loader-2" style={{ animation: "fluid-spin 0.8s linear infinite" }} />
              Loading screenshots…
            </div>
          )}
          {!loadingAttachments && attachments.length > 0 && (
            <div>
              <p style={{ margin: "0 0 8px", fontSize: 10.5, color: T.mutedSoft, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: 0.6 }}>
                Screenshots ({attachments.length})
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {attachments.map((att, i) => (
                  <div key={att.gid || i}>
                    <a href={att.view_url} target="_blank" rel="noreferrer"
                      style={{ display: "block", borderRadius: 8, overflow: "hidden",
                        border: `1px solid ${T.line}`, backgroundColor: T.paper }}>
                      <img src={att.view_url} alt={att.name}
                        style={{ width: "100%", height: "auto", display: "block", maxHeight: 400, objectFit: "contain" }}
                        onError={e => {
                          e.target.style.display = "none";
                          e.target.parentNode.innerHTML = `<div style="padding:20px;text-align:center;color:${T.muted};font-size:13px">📎 ${att.name} — click to open</div>`;
                        }}
                      />
                    </a>
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: T.mutedSoft }}>{att.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {issue.tracking && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <p style={{ margin: "0 0 8px", fontSize: 12, color: T.inkSoft, fontWeight: 700 }}>Update status</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  {STATUSES.map(s => {
                    const isActive = issue.status === s;
                    const style = STATUS_STYLE[s];
                    return (
                      <button key={s} onClick={() => onStatusChange(issue.id, s)} style={{
                        padding: "6px 13px", borderRadius: 7, fontSize: 12.5,
                        border: `1.5px solid ${isActive ? style.line : T.line}`,
                        backgroundColor: isActive ? style.bg : T.paper,
                        color: isActive ? style.text : T.muted,
                        cursor: "pointer", fontWeight: isActive ? 700 : 500,
                        fontFamily: FONT_SANS, transition: "all 0.12s ease",
                      }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{
                  flex: 1, backgroundColor: T.paper, borderRadius: 8, padding: "9px 12px",
                  fontSize: 12, display: "flex", gap: 7, alignItems: "center", color: T.inkSoft, fontFamily: FONT_MONO,
                }}>
                  <i className="ti ti-brand-asana" style={{ color: "#F06A6A", fontFamily: FONT_SANS }} />
                  {issue.id}
                </div>
                {issue.gsRow && (
                  <div style={{
                    flex: 1, backgroundColor: T.paper, borderRadius: 8, padding: "9px 12px",
                    fontSize: 12, display: "flex", gap: 7, alignItems: "center", color: T.inkSoft, fontFamily: FONT_MONO,
                  }}>
                    <i className="ti ti-table" style={{ color: T.green, fontFamily: FONT_SANS }} />
                    Sheet row {issue.gsRow}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// ── Google Sheet view ─────────────────────────────────────────────────────────

function GoogleSheetView({ issues }) {
  const tracked = issues.filter(i => i.tracking);

  // Column definitions with explicit widths so nothing gets squashed
  const cols = [
    { label: "#",          width: 40  },
    { label: "Title",      width: 260 },
    { label: "Type",       width: 80  },
    { label: "Severity",   width: 90  },
    { label: "Priority",   width: 70  },
    { label: "Impact",     width: 120 },
    { label: "Status",     width: 100 },
    { label: "Reporter",   width: 130 },
    { label: "Reported at",width: 150 },
    { label: "Channel",    width: 140 },
    { label: "Module",     width: 90  },
    { label: "SME",        width: 110 },
    { label: "SME notes",  width: 180 },
    { label: "Asana task", width: 100 },
    { label: "Slack link", width: 80  },
  ];
  const totalWidth = cols.reduce((s, c) => s + c.width, 0);

  const cellStyle = (width, extra = {}) => ({
    padding: "9px 12px",
    fontSize: 12,
    color: T.inkSoft,
    borderRight: `1px solid ${T.line}`,
    width,
    maxWidth: width,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    ...extra,
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{ width: 32, height: 32, backgroundColor: "#0F9D58", borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>G</span>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: T.ink }}>Fluid Platform Tracker</p>
            <p style={{ margin: "1px 0 0", fontSize: 12, color: T.muted }}>{tracked.length} rows synced</p>
          </div>
        </div>
        <span style={{ fontSize: 11.5, color: T.green, backgroundColor: T.greenBg, fontWeight: 700,
          padding: "5px 11px", borderRadius: 20, border: `1px solid ${T.greenLine}`,
          display: "inline-flex", alignItems: "center", gap: 6 }}>
          <PulseDot color={T.green} />Live sync
        </span>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", border: `1px solid ${T.line}`, borderRadius: 12 }}>
        <table style={{ borderCollapse: "collapse", width: totalWidth, tableLayout: "fixed" }}>
          <colgroup>
            {cols.map(c => <col key={c.label} style={{ width: c.width }} />)}
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: T.ink }}>
              {cols.map(c => (
                <th key={c.label} style={{
                  padding: "10px 12px", fontSize: 11, fontWeight: 700, color: T.paperRaised,
                  textAlign: "left", whiteSpace: "nowrap", letterSpacing: 0.4,
                  borderRight: "1px solid rgba(255,255,255,0.1)",
                }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tracked.length === 0 && (
              <tr>
                <td colSpan={cols.length} style={{ padding: 36, textAlign: "center",
                  color: T.muted, fontSize: 13.5, backgroundColor: T.paperRaised }}>
                  Nothing here yet — validate an issue and mark it for tracking to populate this view.
                </td>
              </tr>
            )}
            {tracked.map((issue, idx) => {
              const sev = issue.severity ? SEVERITY[issue.severity] : null;
              const ss  = STATUS_STYLE[issue.status] || STATUS_STYLE["New"];
              const rowBg = idx % 2 === 0 ? T.paperRaised : T.brandBgLight;
              return (
                <tr key={issue.id} style={{ backgroundColor: rowBg, borderBottom: `1px solid ${T.line}` }}>
                  {/* # */}
                  <td style={cellStyle(40, { color: T.mutedSoft, fontFamily: FONT_MONO, fontSize: 11 })}>{idx + 2}</td>
                  {/* Title */}
                  <td style={cellStyle(260, { color: T.ink, fontWeight: 500, fontSize: 12.5 })} title={issue.title}>
                    {issue.title}
                  </td>
                  {/* Type */}
                  <td style={cellStyle(80)}>
                    <Badge
                      label={issue.type === "bug" ? "Bug" : "Feature"}
                      bg={issue.type === "bug" ? T.redBg : T.purpleBg}
                      line={issue.type === "bug" ? T.redLine : T.purpleLine}
                      text={issue.type === "bug" ? T.red : T.purple}
                    />
                  </td>
                  {/* Severity */}
                  <td style={cellStyle(90)}>
                    {sev
                      ? <Badge label={sev.label} bg={sev.bg} line={sev.line} text={sev.text} />
                      : <span style={{ color: T.mutedSoft }}>—</span>}
                  </td>
                  {/* Priority */}
                  <td style={cellStyle(70, { fontFamily: FONT_MONO, fontWeight: 700, color: T.inkSoft })}>
                    {issue.priority || "—"}
                  </td>
                  {/* Impact */}
                  <td style={cellStyle(120)}>
                    {issue.impact
                      ? <Badge label={IMPACT[issue.impact]?.label || issue.impact} bg={T.tealBg} line={T.tealLine} text={T.teal} />
                      : <span style={{ color: T.mutedSoft }}>—</span>}
                  </td>
                  {/* Status */}
                  <td style={cellStyle(100)}>
                    <Badge label={issue.status} bg={ss.bg} line={ss.line} text={ss.text} />
                  </td>
                  {/* Reporter */}
                  <td style={cellStyle(130)} title={issue.reporter}>{issue.reporter || "—"}</td>
                  {/* Reported at */}
                  <td style={cellStyle(150, { fontFamily: FONT_MONO, fontSize: 11, color: T.muted, whiteSpace: "nowrap" })}>
                    {formatDate(issue.reportedAt)}
                  </td>
                  {/* Channel */}
                  <td style={cellStyle(140, { color: T.muted, fontSize: 11.5 })}>{issue.channel}</td>
                  {/* Module */}
                  <td style={cellStyle(90, { fontSize: 11.5 })}>{issue.module || "—"}</td>
                  {/* SME */}
                  <td style={cellStyle(110)} title={issue.smeName}>{issue.smeName || "—"}</td>
                  {/* SME notes */}
                  <td style={cellStyle(180, { color: T.muted, fontSize: 11.5 })} title={issue.smeNote}>
                    {issue.smeNote || "—"}
                  </td>
                  {/* Asana task */}
                  <td style={cellStyle(100)}>
                    <a href={`https://app.asana.com/0/0/${issue.asanaTaskGid}`}
                      target="_blank" rel="noreferrer"
                      style={{ color: T.brand, fontSize: 11.5, fontWeight: 600, textDecoration: "none" }}>
                      Open
                    </a>
                  </td>
                  {/* Slack link */}
                  <td style={cellStyle(80, { borderRight: "none" })}>
                    {issue.slackUrl
                      ? <a href={issue.slackUrl} target="_blank" rel="noreferrer"
                          style={{ color: T.amber, fontSize: 11.5, fontWeight: 600, textDecoration: "none" }}>
                          Open
                        </a>
                      : <span style={{ color: T.mutedSoft }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, accent }) {
  return (
    <div style={{
      backgroundColor: T.paperRaised, border: `1px solid ${T.line}`, borderRadius: 12,
      padding: "16px 17px", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: "100%", backgroundColor: accent }} />
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 9 }}>
        <i className={`ti ${icon}`} style={{ fontSize: 15, color: accent }} />
        <p style={{ margin: 0, fontSize: 11.5, color: T.muted, fontWeight: 600, letterSpacing: 0.1 }}>{label}</p>
      </div>
      <p style={{ margin: 0, fontSize: 30, fontWeight: 800, color: T.ink, fontFamily: FONT_MONO, letterSpacing: -0.5 }}>{value}</p>
    </div>
  );
}

function Dashboard({ issues }) {
  const bugs     = issues.filter(i => i.type === "bug").length;
  const features = issues.filter(i => i.type === "feature").length;
  const tracked  = issues.filter(i => i.tracking).length;
  const critical = issues.filter(i => i.severity === "critical").length;
  const byModule = MODULES.map(m => ({ module: m, count: issues.filter(i => i.module === m).length })).filter(x => x.count > 0);
  const byStatus = STATUSES.map(s => ({ status: s, count: issues.filter(i => i.status === s && i.tracking).length })).filter(x => x.count > 0);
  const maxModule = Math.max(...byModule.map(x => x.count), 1);

  // "Not a bug" issues — validated by SME as not requiring a fix
  const notABugIssues = issues.filter(i => i.severity === "not_a_bug");

  // Group not-a-bug issues by reporter so SME can identify training needs
  const byAgent = notABugIssues.reduce((acc, issue) => {
    const agent = issue.reporter || "Unknown";
    if (!acc[agent]) acc[agent] = [];
    acc[agent].push(issue);
    return acc;
  }, {});
  const agentList = Object.entries(byAgent).sort((a, b) => b[1].length - a[1].length);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10 }}>
        <StatCard label="Total issues" value={issues.length} icon="ti-list-details" accent={T.ink} />
        <StatCard label="Bugs" value={bugs} icon="ti-bug" accent={T.red} />
        <StatCard label="Features" value={features} icon="ti-sparkles" accent={T.purple} />
        <StatCard label="Tracked" value={tracked} icon="ti-chart-line" accent={T.teal} />
        <StatCard label="Critical" value={critical} icon="ti-flame" accent={T.amber} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 14 }}>
        <div style={{ backgroundColor: T.paperRaised, border: `1px solid ${T.line}`, borderRadius: 14, padding: 20 }}>
          <p style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, color: T.ink }}>Issues by module</p>
          {byModule.map(({ module, count }) => (
            <div key={module} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 12.5, color: T.inkSoft, fontWeight: 600 }}>{module}</span>
                <span style={{ fontSize: 12, color: T.muted, fontFamily: FONT_MONO }}>{count}</span>
              </div>
              <div style={{ height: 7, backgroundColor: T.sand, borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  height: 7, backgroundColor: T.brand, borderRadius: 4,
                  width: `${(count / maxModule) * 100}%`, transition: "width 0.4s ease",
                }} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ backgroundColor: T.paperRaised, border: `1px solid ${T.line}`, borderRadius: 14, padding: 20 }}>
          <p style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, color: T.ink }}>Tracked by status</p>
          {byStatus.length === 0 && (
            <p style={{ fontSize: 13, color: T.mutedSoft, lineHeight: 1.6 }}>No tracked issues yet — once an SME marks an issue for tracking, its status will appear here.</p>
          )}
          {byStatus.map(({ status, count }) => {
            const ss = STATUS_STYLE[status];
            return (
              <div key={status} style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 11 }}>
                <div style={{ width: 9, height: 9, borderRadius: 3, backgroundColor: ss.text, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, flex: 1, color: T.inkSoft, fontWeight: 500 }}>{status}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.ink, fontFamily: FONT_MONO }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Not a bug — agent training section ──────────────────────────── */}
      <div style={{
        backgroundColor: T.paperRaised, border: `1px solid ${T.line}`,
        borderRadius: 14, overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 20px", borderBottom: `1px solid ${T.line}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, backgroundColor: T.greenBg,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: `1px solid ${T.greenLine}`,
            }}>
              <i className="ti ti-school" style={{ fontSize: 16, color: T.green }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.ink }}>Agent training insights</p>
              <p style={{ margin: "1px 0 0", fontSize: 12, color: T.muted }}>
                Issues marked "Not a bug" by SME — use these to coach agents
              </p>
            </div>
          </div>
          <span style={{
            fontSize: 12, fontWeight: 700, color: T.green, backgroundColor: T.greenBg,
            padding: "4px 10px", borderRadius: 10, border: `1px solid ${T.greenLine}`,
            fontFamily: FONT_MONO,
          }}>
            {notABugIssues.length} total
          </span>
        </div>

        {notABugIssues.length === 0 ? (
          <div style={{ padding: "28px 20px", textAlign: "center", color: T.mutedSoft, fontSize: 13 }}>
            <i className="ti ti-circle-check" style={{ fontSize: 22, display: "block", marginBottom: 8, color: T.green }} />
            No issues marked as "Not a bug" yet — they'll appear here once SMEs start reviewing.
          </div>
        ) : (
          <div>
            {/* Agent summary strip */}
            {agentList.length > 0 && (
              <div style={{
                padding: "12px 20px", backgroundColor: T.paper,
                borderBottom: `1px solid ${T.line}`,
                display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
              }}>
                <span style={{ fontSize: 11.5, color: T.muted, fontWeight: 600, marginRight: 4 }}>By agent:</span>
                {agentList.map(([agent, items]) => (
                  <span key={agent} style={{
                    fontSize: 12, fontWeight: 600, color: T.amber,
                    backgroundColor: T.amberBg, padding: "3px 10px",
                    borderRadius: 10, border: `1px solid ${T.amberLine}`,
                    display: "inline-flex", alignItems: "center", gap: 5,
                  }}>
                    <i className="ti ti-user" style={{ fontSize: 11 }} />
                    {agent}
                    <span style={{
                      backgroundColor: T.amber, color: T.paperRaised,
                      borderRadius: 8, fontSize: 10.5, fontWeight: 700,
                      padding: "1px 5px", fontFamily: FONT_MONO,
                    }}>
                      {items.length}
                    </span>
                  </span>
                ))}
              </div>
            )}

            {/* Issue rows table */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: T.sand }}>
                  {["Agent", "Channel", "Issue reported", "Reported at", "SME who reviewed"].map(col => (
                    <th key={col} style={{
                      padding: "8px 16px", fontSize: 11, fontWeight: 700, color: T.muted,
                      textAlign: "left", letterSpacing: 0.3, borderBottom: `1px solid ${T.line}`,
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {notABugIssues
                  .sort((a, b) => new Date(b.reportedAt) - new Date(a.reportedAt))
                  .map((issue, idx) => (
                  <tr key={issue.id} style={{
                    backgroundColor: idx % 2 === 0 ? T.paperRaised : T.paper,
                    borderBottom: `1px solid ${T.line}`,
                  }}>
                    {/* Agent name */}
                    <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 600, color: T.ink, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <div style={{
                          width: 26, height: 26, borderRadius: "50%", backgroundColor: T.amberBg,
                          border: `1px solid ${T.amberLine}`, display: "flex", alignItems: "center",
                          justifyContent: "center", flexShrink: 0,
                        }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: T.amber }}>
                            {(issue.reporter || "?").charAt(0).toUpperCase()}
                          </span>
                        </div>
                        {issue.reporter || "Unknown"}
                      </div>
                    </td>
                    {/* Channel */}
                    <td style={{ padding: "10px 16px", fontSize: 12, color: T.muted, whiteSpace: "nowrap" }}>
                      {issue.channel}
                    </td>
                    {/* Issue title */}
                    <td style={{ padding: "10px 16px", fontSize: 13, color: T.inkSoft, maxWidth: 340 }}>
                      <span style={{
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {issue.title}
                      </span>
                    </td>
                    {/* Timestamp */}
                    <td style={{ padding: "10px 16px", whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.inkSoft, fontFamily: FONT_MONO, display: "block" }}>
                        {formatDate(issue.reportedAt)}
                      </span>
                      <span style={{ fontSize: 11, color: T.mutedSoft }}>
                        {timeAgo(issue.reportedAt)}
                      </span>
                    </td>
                    {/* SME who reviewed */}
                    <td style={{ padding: "10px 16px", fontSize: 12, color: T.inkSoft, whiteSpace: "nowrap" }}>
                      {issue.smeName ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <i className="ti ti-shield-check" style={{ fontSize: 13, color: T.teal }} />
                          {issue.smeName}
                        </span>
                      ) : (
                        <span style={{ color: T.mutedSoft }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Connection error ──────────────────────────────────────────────────────────

function ConnectionError({ apiUrl, errorDetail, onRetry, retrying }) {
  return (
    <div style={{
      backgroundColor: T.paperRaised, border: `1px solid ${T.redLine}`, borderRadius: 14,
      padding: "24px 26px", color: T.inkSoft, borderTop: `3px solid ${T.red}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, backgroundColor: T.redBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className="ti ti-plug-connected-x" style={{ fontSize: 17, color: T.red }} />
        </div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.ink }}>Can't reach the backend server</p>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 13.5, lineHeight: 1.65 }}>
        The tracker tried to load issues from <code style={{ backgroundColor: T.sand, padding: "2px 7px", borderRadius: 5, fontFamily: FONT_MONO, fontSize: 12.5 }}>{apiUrl}/api/tasks</code> and got no response.
        {errorDetail && <> Error detail: <em>{errorDetail}</em></>}
      </p>
      <div style={{ backgroundColor: T.paper, borderRadius: 11, padding: "16px 18px", marginBottom: 16 }}>
        <p style={{ margin: "0 0 11px", fontSize: 13, fontWeight: 700, color: T.ink }}>Check these in order</p>
        <ol style={{ margin: 0, paddingLeft: 19, fontSize: 13, color: T.inkSoft, lineHeight: 2 }}>
          <li>Open the terminal where you ran <code style={{ fontFamily: FONT_MONO, fontSize: 12 }}>node server.js</code>. Is it still showing <strong>"⚡ Fluid Ops Bot is running!"</strong>? If that terminal closed, reopen it and run the command again.</li>
          <li>Visit <a href={`${apiUrl}/health`} target="_blank" rel="noreferrer" style={{ color: T.teal, fontWeight: 600 }}>{apiUrl}/health</a> directly. If that fails too, the server isn't running.</li>
          <li>If running two ngrok tunnels at once, both must use <code style={{ fontFamily: FONT_MONO, fontSize: 12 }}>--pooling-enabled</code> and show two different forwarding URLs.</li>
          <li>This page should be opened at <code style={{ fontFamily: FONT_MONO, fontSize: 12 }}>http://localhost:3000</code> directly while testing locally — not through ngrok.</li>
        </ol>
      </div>
      <button onClick={onRetry} disabled={retrying} style={{
        padding: "9px 18px", borderRadius: 8, border: "none", backgroundColor: T.red,
        color: "#fff", cursor: retrying ? "default" : "pointer", fontSize: 13, fontWeight: 700,
        display: "inline-flex", alignItems: "center", gap: 7, fontFamily: FONT_SANS,
      }}>
        <i className={`ti ${retrying ? "ti-loader-2" : "ti-refresh"}`} style={retrying ? { animation: "fluid-spin 0.8s linear infinite" } : {}} />
        {retrying ? "Retrying…" : "Retry connection"}
      </button>
    </div>
  );
}


// ── SME Dashboard ─────────────────────────────────────────────────────────────
// Dedicated view for Govarthan M and Shameer S to see their review output:
// how many issues were categorised into each bucket, with drill-down on click.

const SME_REVIEW_BUCKETS = [
  {
    key: "critical",
    label: "Critical",
    icon: "ti-flame",
    bg: T.redBg, line: T.redLine, text: T.red,
    match: i => i.severity === "critical",
    desc: "Issues that are critical — requires immediate product team attention",
  },
  {
    key: "high",
    label: "High",
    icon: "ti-alert-triangle",
    bg: T.amberBg, line: T.amberLine, text: T.amber,
    match: i => i.severity === "high",
    desc: "High severity issues — should be addressed this sprint",
  },
  {
    key: "medium",
    label: "Medium",
    icon: "ti-alert-circle",
    bg: T.sand, line: T.lineStrong, text: T.inkSoft,
    match: i => i.severity === "medium",
    desc: "Medium severity — next sprint or backlog",
  },
  {
    key: "low",
    label: "Low",
    icon: "ti-info-circle",
    bg: T.tealBg, line: T.tealLine, text: T.teal,
    match: i => i.severity === "low",
    desc: "Low severity — minor issues or cosmetic bugs",
  },
  {
    key: "feature",
    label: "Feature request",
    icon: "ti-sparkles",
    bg: T.purpleBg, line: T.purpleLine, text: T.purple,
    match: i => i.severity === "feature",
    desc: "Feature requests raised by the team for product consideration",
  },
  {
    key: "resolved",
    label: "Resolved",
    icon: "ti-circle-check",
    bg: T.tealBg, line: T.tealLine, text: T.teal,
    match: i => i.severity === "resolved",
    desc: "Issues already fixed by product support before SME review",
  },
  {
    key: "not_a_bug",
    label: "Not a bug",
    icon: "ti-circle-x",
    bg: T.greenBg, line: T.greenLine, text: T.green,
    match: i => i.severity === "not_a_bug",
    desc: "Reported as a bug but identified as expected behaviour or agent error",
  },
  {
    key: "needs_review",
    label: "Needs re-validation",
    icon: "ti-refresh-alert",
    bg: T.amberBg, line: T.amberLine, text: T.amber,
    match: i => i.severity === "needs_review",
    desc: "Previously tracked issues migrated before validation data was recorded — SMEs should re-validate these to set the correct severity",
  },
  {
    key: "pending",
    label: "Pending review",
    icon: "ti-clock",
    bg: T.sand, line: T.lineStrong, text: T.muted,
    match: i => !i.severity,
    desc: "Issues not yet reviewed by an SME",
  },
];

function SmeDashboard({ issues, onViewIssue }) {
  const [drillBucket, setDrillBucket] = useState(null); // key of bucket being drilled into
  const [drillSme, setDrillSme]       = useState("all"); // filter drill by SME name

  // Per-SME breakdown
  const smeNames = [...new Set(
    issues.filter(i => i.smeName).map(i => i.smeName)
  )].sort();

  const reviewedIssues = issues.filter(i => i.severity);
  const totalReviewed  = reviewedIssues.length;
  const totalPending   = issues.filter(i => !i.severity).length;

  // Per-SME stats
  const smeStats = smeNames.map(name => ({
    name,
    total:    issues.filter(i => i.smeName === name).length,
    notABug:  issues.filter(i => i.smeName === name && i.severity === "not_a_bug").length,
    resolved: issues.filter(i => i.smeName === name && i.severity === "resolved").length,
    critical: issues.filter(i => i.smeName === name && i.severity === "critical").length,
    tracked:  issues.filter(i => i.smeName === name && i.tracking).length,
  }));

  // Drill detail list
  const drillBucketObj = SME_REVIEW_BUCKETS.find(b => b.key === drillBucket);
  const drillIssues = drillBucket
    ? issues
        .filter(drillBucketObj.match)
        .filter(i => drillSme === "all" || i.smeName === drillSme)
        .sort((a, b) => new Date(b.reportedAt) - new Date(a.reportedAt))
    : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── Top summary strip ────────────────────────────────────────────── */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10,
      }}>
        {[
          ["Total issues", issues.length, "ti-list-details", T.ink, T.sand, T.lineStrong],
          ["Reviewed by SME", totalReviewed, "ti-shield-check", T.teal, T.tealBg, T.tealLine],
          ["Pending review", totalPending, "ti-clock", T.amber, T.amberBg, T.amberLine],
        ].map(([label, val, icon, text, bg, line]) => (
          <div key={label} style={{
            backgroundColor: bg, border: `1px solid ${line}`, borderRadius: 12,
            padding: "14px 16px", position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: "100%", backgroundColor: text }} />
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
              <i className={`ti ${icon}`} style={{ fontSize: 15, color: text }} />
              <p style={{ margin: 0, fontSize: 11.5, color: text, fontWeight: 600 }}>{label}</p>
            </div>
            <p style={{ margin: 0, fontSize: 30, fontWeight: 800, color: text, fontFamily: FONT_MONO, letterSpacing: -0.5 }}>{val}</p>
          </div>
        ))}
      </div>

      {/* ── Bucket cards — click to drill in ──────────────────────────────── */}
      <div>
        <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: T.ink }}>
          Review outcome breakdown
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
          {SME_REVIEW_BUCKETS.map(bucket => {
            const count = issues.filter(bucket.match).length;
            const isActive = drillBucket === bucket.key;
            return (
              <button
                key={bucket.key}
                onClick={() => setDrillBucket(isActive ? null : bucket.key)}
                style={{
                  backgroundColor: isActive ? bucket.bg : T.paperRaised,
                  border: `${isActive ? "2px" : "1px"} solid ${isActive ? bucket.line : T.line}`,
                  borderRadius: 12, padding: "14px 14px 12px",
                  cursor: count > 0 ? "pointer" : "default",
                  textAlign: "left", transition: "all 0.15s ease",
                  boxShadow: isActive ? `0 0 0 3px ${bucket.bg}` : "none",
                  opacity: count === 0 ? 0.45 : 1,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    backgroundColor: bucket.bg, border: `1px solid ${bucket.line}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className={`ti ${bucket.icon}`} style={{ fontSize: 15, color: bucket.text }} />
                  </div>
                  {isActive && (
                    <i className="ti ti-chevron-up" style={{ fontSize: 14, color: bucket.text }} />
                  )}
                </div>
                <p style={{
                  margin: "0 0 2px", fontSize: 26, fontWeight: 800,
                  color: count > 0 ? bucket.text : T.mutedSoft,
                  fontFamily: FONT_MONO, letterSpacing: -0.5,
                }}>
                  {count}
                </p>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: count > 0 ? bucket.text : T.muted }}>
                  {bucket.label}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Drill-down panel ─────────────────────────────────────────────── */}
      {drillBucket && drillBucketObj && (
        <div style={{
          backgroundColor: T.paperRaised,
          border: `1.5px solid ${drillBucketObj.line}`,
          borderRadius: 14, overflow: "hidden",
          animation: "none",
        }}>
          {/* Drill header */}
          <div style={{
            padding: "14px 18px", borderBottom: `1px solid ${T.line}`,
            backgroundColor: drillBucketObj.bg,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 9,
                backgroundColor: T.paperRaised, border: `1px solid ${drillBucketObj.line}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className={`ti ${drillBucketObj.icon}`} style={{ fontSize: 17, color: drillBucketObj.text }} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: drillBucketObj.text }}>
                  {drillBucketObj.label}
                  <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 8, fontFamily: FONT_MONO }}>
                    ({drillIssues.length})
                  </span>
                </p>
                <p style={{ margin: "1px 0 0", fontSize: 12, color: drillBucketObj.text, opacity: 0.75 }}>
                  {drillBucketObj.desc}
                </p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* SME filter inside drill */}
              <select
                value={drillSme}
                onChange={e => setDrillSme(e.target.value)}
                style={{
                  fontSize: 12.5, padding: "5px 10px", borderRadius: 7,
                  border: `1px solid ${drillBucketObj.line}`,
                  backgroundColor: T.paperRaised, color: T.inkSoft,
                  fontFamily: FONT_SANS, fontWeight: 500,
                }}
              >
                <option value="all">All SMEs</option>
                {smeNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                onClick={() => setDrillBucket(null)}
                style={{
                  background: T.paperRaised, border: `1px solid ${drillBucketObj.line}`,
                  borderRadius: 7, width: 28, height: 28, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: drillBucketObj.text, fontSize: 14,
                }}
              >
                <i className="ti ti-x" />
              </button>
            </div>
          </div>

          {/* Drill issue table */}
          {drillIssues.length === 0 ? (
            <div style={{ padding: "28px 18px", textAlign: "center", color: T.mutedSoft, fontSize: 13 }}>
              No issues in this category{drillSme !== "all" ? ` for ${drillSme}` : ""}.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: T.sand }}>
                  {["Issue", "Channel", "Reporter", "Reported at", "SME", "Notes"].map(col => (
                    <th key={col} style={{
                      padding: "8px 14px", fontSize: 11, fontWeight: 700,
                      color: T.muted, textAlign: "left", letterSpacing: 0.3,
                      borderBottom: `1px solid ${T.line}`,
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {drillIssues.map((issue, idx) => (
                  <tr
                    key={issue.id}
                    onClick={() => onViewIssue(issue)}
                    style={{
                      backgroundColor: idx % 2 === 0 ? T.paperRaised : T.paper,
                      borderBottom: `1px solid ${T.line}`,
                      cursor: "pointer",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = drillBucketObj.bg}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? T.paperRaised : T.paper}
                  >
                    {/* Issue title */}
                    <td style={{ padding: "10px 14px", maxWidth: 280 }}>
                      <p style={{
                        margin: 0, fontSize: 13, fontWeight: 600, color: T.ink,
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical", overflow: "hidden",
                        lineHeight: 1.4,
                      }}>
                        {issue.title}
                      </p>
                      <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                        <Badge
                          label={issue.type === "bug" ? "Bug" : "Feature"}
                          bg={issue.type === "bug" ? T.redBg : T.purpleBg}
                          line={issue.type === "bug" ? T.redLine : T.purpleLine}
                          text={issue.type === "bug" ? T.red : T.purple}
                        />
                        <Badge label={issue.module || "Platform"} bg={T.sand} line={T.lineStrong} text={T.inkSoft} />
                      </div>
                    </td>
                    {/* Channel */}
                    <td style={{ padding: "10px 14px", fontSize: 12, color: T.muted, whiteSpace: "nowrap" }}>
                      {issue.channel}
                    </td>
                    {/* Reporter */}
                    <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500, color: T.inkSoft, whiteSpace: "nowrap" }}>
                      {issue.reporter || "—"}
                    </td>
                    {/* Timestamp */}
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.inkSoft, fontFamily: FONT_MONO, display: "block" }}>
                        {formatDate(issue.reportedAt)}
                      </span>
                      <span style={{ fontSize: 11, color: T.mutedSoft }}>{timeAgo(issue.reportedAt)}</span>
                    </td>
                    {/* SME */}
                    <td style={{ padding: "10px 14px", fontSize: 12, color: T.inkSoft, whiteSpace: "nowrap" }}>
                      {issue.smeName
                        ? <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <i className="ti ti-shield-check" style={{ color: T.teal, fontSize: 13 }} />
                            {issue.smeName}
                          </span>
                        : <span style={{ color: T.mutedSoft }}>—</span>
                      }
                    </td>
                    {/* SME notes */}
                    <td style={{ padding: "10px 14px", fontSize: 12, color: T.muted, maxWidth: 200 }}>
                      <span style={{
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {issue.smeNote || <span style={{ color: T.mutedSoft }}>—</span>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Per-SME performance breakdown ─────────────────────────────────── */}
      {smeStats.length > 0 && (
        <div style={{
          backgroundColor: T.paperRaised, border: `1px solid ${T.line}`,
          borderRadius: 14, overflow: "hidden",
        }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.line}` }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.ink }}>SME performance</p>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: T.muted }}>Issues reviewed per team member</p>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: T.sand }}>
                {["SME", "Total reviewed", "Critical", "Not a bug", "Resolved", "Tracked to product"].map(col => (
                  <th key={col} style={{
                    padding: "8px 16px", fontSize: 11, fontWeight: 700,
                    color: T.muted, textAlign: "left", letterSpacing: 0.3,
                    borderBottom: `1px solid ${T.line}`,
                  }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {smeStats.map((s, idx) => (
                <tr key={s.name} style={{
                  backgroundColor: idx % 2 === 0 ? T.paperRaised : T.paper,
                  borderBottom: `1px solid ${T.line}`,
                }}>
                  <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 700, color: T.ink }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: "50%",
                        backgroundColor: T.brandBg, border: `1px solid ${T.brand}`,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: T.teal }}>
                          {s.name.charAt(0)}
                        </span>
                      </div>
                      {s.name}
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{
                      fontSize: 16, fontWeight: 800, color: T.ink,
                      fontFamily: FONT_MONO,
                    }}>{s.total}</span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {s.critical > 0
                      ? <span style={{ fontSize: 13, fontWeight: 700, color: T.red, fontFamily: FONT_MONO }}>{s.critical}</span>
                      : <span style={{ fontSize: 13, color: T.mutedSoft }}>—</span>
                    }
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {s.notABug > 0
                      ? <span style={{ fontSize: 13, fontWeight: 700, color: T.green, fontFamily: FONT_MONO }}>{s.notABug}</span>
                      : <span style={{ fontSize: 13, color: T.mutedSoft }}>—</span>
                    }
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {s.resolved > 0
                      ? <span style={{ fontSize: 13, fontWeight: 700, color: T.teal, fontFamily: FONT_MONO }}>{s.resolved}</span>
                      : <span style={{ fontSize: 13, color: T.mutedSoft }}>—</span>
                    }
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {s.tracked > 0
                      ? <span style={{ fontSize: 13, fontWeight: 700, color: T.purple, fontFamily: FONT_MONO }}>{s.tracked}</span>
                      : <span style={{ fontSize: 13, color: T.mutedSoft }}>—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}

// ── Main app ───────────────────────────────────────────────────────────────────

export default function App() {
  // ── Auth state ──────────────────────────────────────────────────────────
  const [authView, setAuthView]     = useState("login"); // "login" | "signup" | "pending"
  const [authUser, setAuthUser]     = useState(() => {
    try { return JSON.parse(localStorage.getItem("fluid_auth") || "null"); } catch { return null; }
  });
  const [authForm, setAuthForm]     = useState({ name: "", email: "", password: "" });
  const [authError, setAuthError]   = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [issues, setIssues]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [retrying, setRetrying]     = useState(false);
  const [tab, setTab]               = useState("issues");
  const [filterChannel, setFilterChannel] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAgent, setFilterAgent]   = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo]     = useState("");
  const [validateTarget, setValidateTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [toast, setToast]           = useState(null);
  const toastTimer = useRef(null);

  // ── Auth handlers ────────────────────────────────────────────────────────
  const handleLogin = async () => {
    setAuthError(""); setAuthLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authForm.email, password: authForm.password }),
      });
      const data = await res.json();
      if (!res.ok) { setAuthError(data.error || "Login failed"); return; }
      localStorage.setItem("fluid_auth", JSON.stringify(data.user));
      setAuthUser(data.user);
    } catch { setAuthError("Could not reach the server. Check your connection."); }
    finally { setAuthLoading(false); }
  };

  const handleSignup = async () => {
    setAuthError(""); setAuthLoading(true);
    try {
      const res = await fetch(`${API}/auth/signup`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: authForm.name, email: authForm.email, password: authForm.password }),
      });
      const data = await res.json();
      if (!res.ok) { setAuthError(data.error || "Signup failed"); return; }
      setAuthView("pending");
    } catch { setAuthError("Could not reach the server."); }
    finally { setAuthLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("fluid_auth");
    setAuthUser(null);
    setAuthView("login");
    setAuthForm({ name: "", email: "", password: "" });
  };

  const loadTasks = () => {
    setFetchError(null);
    return fetch(`${API}/api/tasks`)
      .then(res => {
        if (!res.ok) throw new Error(`Server responded with status ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data.error) throw new Error(data.error);
        setIssues(data.tasks || []);
      })
      .catch(err => {
        console.error("Failed to fetch tasks from backend:", err);
        setFetchError(err.message || "Network error — server may not be running");
      });
  };

  useEffect(() => {
    setLoading(true);
    loadTasks().finally(() => setLoading(false));
  }, []);

  const handleRetry = () => {
    setRetrying(true);
    loadTasks().finally(() => setRetrying(false));
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  };

  const handleValidateSave = async (data) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          asanaTaskGid: validateTarget.asanaTaskGid || validateTarget.id,
          severity: data.severity, impact: data.impact, issueType: data.issueType,
          smeName: data.smeName, smeNote: data.smeNote, tracking: data.tracking,
          module: validateTarget.module, title: validateTarget.title,
          channel: validateTarget.channel, reporter: validateTarget.reporter,
          reportedAt: validateTarget.reportedAt, type: validateTarget.type,
        }),
      });
      if (!res.ok) throw new Error(`Server responded with status ${res.status}`);
      const result = await res.json();
      setIssues(prev => prev.map(i =>
        i.id === validateTarget.id
          ? { ...i, ...data, tracking: data.tracking, status: data.tracking ? "In Progress" : i.status, gsRow: result.gsRow }
          : i
      ));
      setValidateTarget(null);
      showToast(data.tracking ? "Validated & synced to Google Sheet" : "Validated and saved", "success");
    } catch (err) {
      showToast(`Save failed: ${err.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    const issue = issues.find(i => i.id === id);
    try {
      const res = await fetch(`${API}/api/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ asanaTaskGid: issue.asanaTaskGid || issue.id, gsRow: issue.gsRow, status }),
      });
      if (!res.ok) throw new Error(`Server responded with status ${res.status}`);
      setIssues(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      if (viewTarget?.id === id) setViewTarget(v => ({ ...v, status }));
      showToast(`Status updated to "${status}"`, "success");
    } catch (err) {
      showToast(`Status update failed: ${err.message}`, "error");
    }
  };

  // Build unique sorted agent list from all loaded issues for the dropdown
  const agentOptions = [...new Set(issues.map(i => i.reporter).filter(Boolean))].sort();

  // ── Delete issue ──────────────────────────────────────────────────────────
  const handleDelete = async (issue) => {
    if (!window.confirm(`Delete "${issue.title.slice(0,60)}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/task/${issue.asanaTaskGid || issue.id}/delete`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`Server responded with status ${res.status}`);
      setIssues(prev => prev.filter(i => i.id !== issue.id));
      if (viewTarget?.id === issue.id) setViewTarget(null);
      showToast("Issue deleted", "success");
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, "error");
    }
  };

  const filtered = issues.filter(i => {
    if (filterChannel !== "all" && i.channel !== filterChannel) return false;
    if (filterType    !== "all" && i.type    !== filterType)    return false;
    if (filterStatus  !== "all" && i.status  !== filterStatus)  return false;
    if (filterAgent   !== "all" && i.reporter !== filterAgent)  return false;
    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      from.setHours(0, 0, 0, 0);
      if (new Date(i.reportedAt) < from) return false;
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(i.reportedAt) > to) return false;
    }
    return true;
  });

  const unvalidatedCount = issues.filter(i => !i.severity && !i.tracking).length;
  const currentList = tab === "sme" ? issues.filter(i => !i.severity && !i.tracking)
    : tab === "tracked" ? issues.filter(i => i.tracking)
    : filtered;

  // ── Auth gate — Fluid branded login / signup screen ──────────────────────────
  if (!authUser) {
    const inputStyle = {
      width: "100%", fontSize: 14, padding: "11px 14px", borderRadius: 10,
      border: `1.5px solid ${T.line}`, backgroundColor: T.paperRaised, color: T.ink,
      boxSizing: "border-box", fontFamily: FONT_SANS, outline: "none",
      transition: "border-color 0.15s",
    };
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #EEE9FF 0%, #F5F2FF 50%, #E8E3FF 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, fontFamily: FONT_SANS,
      }}>
        {/* Decorative background circles matching Fluid app */}
        <div style={{ position: "fixed", top: -120, right: -120, width: 400, height: 400,
          borderRadius: "50%", background: "rgba(61,45,181,0.06)", pointerEvents: "none" }} />
        <div style={{ position: "fixed", bottom: -80, left: -80, width: 300, height: 300,
          borderRadius: "50%", background: "rgba(61,45,181,0.04)", pointerEvents: "none" }} />

        <div style={{ width: "100%", maxWidth: 420, position: "relative" }}>
          {/* Logo */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <img src="/logo.png" alt="Fluid by Pickyourtrail"
              style={{ height: 44, width: "auto", marginBottom: 6 }} />
            <p style={{ margin: 0, fontSize: 12.5, color: T.muted, letterSpacing: 0.3 }}>
              Ops Issue Tracker
            </p>
          </div>

          {/* Card */}
          <div style={{
            backgroundColor: T.paperRaised,
            border: `1px solid ${T.line}`,
            borderRadius: 20,
            padding: "30px 30px 26px",
            boxShadow: "0 8px 40px rgba(61,45,181,0.12)",
          }}>
            {authView === "pending" ? (
              <div style={{ textAlign: "center" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%",
                  background: "linear-gradient(135deg, #EEE9FF, #E8E3FF)",
                  border: `2px solid ${T.brand}`, display: "flex", alignItems: "center",
                  justifyContent: "center", margin: "0 auto 14px" }}>
                  <i className="ti ti-mail-check" style={{ fontSize: 24, color: T.brand }} />
                </div>
                <p style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700, color: T.ink }}>
                  Request sent!
                </p>
                <p style={{ margin: "0 0 22px", fontSize: 13, color: T.muted, lineHeight: 1.7 }}>
                  Your request has been sent to <strong style={{ color: T.inkSoft }}>Akshay Royden</strong> for approval.
                  You'll receive an email once your account is active.
                </p>
                <button onClick={() => setAuthView("login")} style={{
                  fontSize: 13, color: T.brand, background: "none", border: "none",
                  cursor: "pointer", fontWeight: 700, fontFamily: FONT_SANS }}>
                  ← Back to sign in
                </button>
              </div>
            ) : (
              <>
                {/* Tab switcher */}
                <div style={{ display: "flex", backgroundColor: T.brandBg, borderRadius: 10,
                  padding: 3, marginBottom: 24, gap: 3 }}>
                  {[["login","Sign in"],["signup","Request access"]].map(([v, label]) => (
                    <button key={v} onClick={() => { setAuthView(v); setAuthError(""); }}
                      style={{
                        flex: 1, padding: "8px 0", fontSize: 13, fontWeight: authView === v ? 700 : 500,
                        color: authView === v ? T.paperRaised : T.muted,
                        background: authView === v
                          ? `linear-gradient(135deg, ${T.brand}, ${T.brandDark})`
                          : "none",
                        border: "none", borderRadius: 8,
                        cursor: "pointer", fontFamily: FONT_SANS,
                        transition: "all 0.2s", boxShadow: authView === v ? "0 2px 8px rgba(61,45,181,0.3)" : "none",
                      }}>
                      {label}
                    </button>
                  ))}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {authView === "signup" && (
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: T.muted,
                        display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Full name
                      </label>
                      <input value={authForm.name}
                        onChange={e => setAuthForm(p => ({...p, name: e.target.value}))}
                        placeholder="Your full name" style={inputStyle} />
                    </div>
                  )}
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: T.muted,
                      display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Email
                    </label>
                    <input type="email" value={authForm.email}
                      onChange={e => setAuthForm(p => ({...p, email: e.target.value}))}
                      placeholder="you@pickyourtrail.com" style={inputStyle}
                      onKeyDown={e => e.key === "Enter" && (authView === "login" ? handleLogin() : handleSignup())} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: T.muted,
                      display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Password
                    </label>
                    <input type="password" value={authForm.password}
                      onChange={e => setAuthForm(p => ({...p, password: e.target.value}))}
                      placeholder={authView === "signup" ? "Create a password (min 8 chars)" : "Your password"}
                      style={inputStyle}
                      onKeyDown={e => e.key === "Enter" && (authView === "login" ? handleLogin() : handleSignup())} />
                  </div>

                  {authError && (
                    <div style={{ backgroundColor: T.redBg, border: `1px solid ${T.redLine}`,
                      borderRadius: 9, padding: "10px 13px", fontSize: 13, color: T.red,
                      display: "flex", gap: 8, alignItems: "center" }}>
                      <i className="ti ti-alert-circle" style={{ flexShrink: 0 }} />{authError}
                    </div>
                  )}

                  <button
                    onClick={authView === "login" ? handleLogin : handleSignup}
                    disabled={authLoading}
                    style={{
                      marginTop: 4, padding: "12px 0", borderRadius: 10, border: "none",
                      background: authLoading ? T.line : `linear-gradient(135deg, ${T.brand}, ${T.brandDark})`,
                      color: authLoading ? T.muted : T.paperRaised,
                      fontSize: 14.5, fontWeight: 700,
                      cursor: authLoading ? "default" : "pointer", fontFamily: FONT_SANS,
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      boxShadow: authLoading ? "none" : "0 4px 16px rgba(61,45,181,0.35)",
                      transition: "all 0.15s",
                    }}>
                    {authLoading
                      ? <><i className="ti ti-loader-2" style={{ animation: "fluid-spin 0.8s linear infinite" }} />Please wait…</>
                      : authView === "login" ? "Sign in →" : "Request access →"}
                  </button>
                </div>

                {authView === "signup" && (
                  <p style={{ margin: "18px 0 0", fontSize: 12, color: T.muted,
                    textAlign: "center", lineHeight: 1.7, borderTop: `1px solid ${T.line}`, paddingTop: 16 }}>
                    Access is granted by <strong style={{ color: T.inkSoft }}>Akshay Royden</strong>.<br />
                    You'll receive an email confirmation once approved.
                  </p>
                )}
              </>
            )}
          </div>

          <p style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: T.mutedSoft }}>
            Fluid Ops Issue Tracker · Pickyourtrail
          </p>
        </div>
        <style>{`@keyframes fluid-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: FONT_SANS, maxWidth: 920, margin: "0 auto", padding: "1.25rem 0 3rem",
      color: T.ink, backgroundColor: T.brandBg,
    }}>
      <style>{`
        @keyframes fluid-spin { to { transform: rotate(360deg); } }
        @keyframes fluid-pulse {
          0%   { transform: scale(1);   opacity: 0.45; }
          70%  { transform: scale(2.4); opacity: 0; }
          100% { transform: scale(2.4); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; }
        }
        select:focus, input:focus, textarea:focus {
          outline: 2px solid ${T.teal} !important;
          outline-offset: 1px;
        }
        button:focus-visible {
          outline: 2px solid ${T.teal};
          outline-offset: 2px;
        }
      `}</style>

      {toast && (
        <div style={{
          position: "fixed", top: 18, right: 18, zIndex: 999999,
          backgroundColor: T.ink,
          borderRadius: 10, padding: "12px 16px", fontSize: 13,
          color: T.paper, maxWidth: 360, boxShadow: "0 10px 30px rgba(19,21,26,0.35)",
          display: "flex", alignItems: "flex-start", gap: 9, fontFamily: FONT_SANS,
        }}>
          <i className={`ti ${toast.type === "success" ? "ti-circle-check" : "ti-alert-circle"}`}
            style={{ marginTop: 1, color: toast.type === "success" ? "#69DB7C" : "#FF8787", fontSize: 16 }} />
          <span style={{ fontWeight: 500, lineHeight: 1.5 }}>{toast.msg}</span>
        </div>
      )}

      {validateTarget && (
        <ValidateModal issue={validateTarget} saving={saving}
          onClose={() => setValidateTarget(null)} onSave={handleValidateSave} />
      )}
      {viewTarget && (
        <ViewModal
          issue={issues.find(i => i.id === viewTarget.id) || viewTarget}
          onClose={() => setViewTarget(null)}
          onStatusChange={handleStatusChange} />
      )}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src="/logo.png" alt="Fluid by Pickyourtrail"
              style={{ height: 36, width: "auto" }} />
            <div style={{ width: 1, height: 28, backgroundColor: T.line }} />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <PulseDot color={fetchError ? T.red : T.brand} />
                <span style={{ fontSize: 14, fontWeight: 700, color: T.ink, letterSpacing: -0.1 }}>
                  Issue Tracker
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: T.muted }}>
                <span style={{ fontFamily: FONT_MONO }}>Slack → Asana → Sheets</span>
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
            {[["ti-brand-slack", "Slack", T.amberBg, T.amberLine, T.amber],
              ["ti-brand-asana", "Asana", T.redBg, T.redLine, T.red],
              ["ti-table", "Sheets", T.greenBg, T.greenLine, T.green],
            ].map(([icon, label, bg, line, text]) => (
              <span key={label} style={{
                fontSize: 11.5, padding: "5px 11px", borderRadius: 20,
                backgroundColor: bg, border: `1px solid ${line}`, color: text, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 5,
              }}>
                <i className={`ti ${icon}`} />{label}
              </span>
            ))}
            {/* Logged-in user + logout */}
            <div style={{ display: "flex", alignItems: "center", gap: 7,
              backgroundColor: T.sand, border: `1px solid ${T.lineStrong}`,
              borderRadius: 20, padding: "4px 10px 4px 6px" }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: T.tealBg,
                border: `1px solid ${T.tealLine}`, display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: T.brand }}>
                  {(authUser?.name || "U").charAt(0).toUpperCase()}
                </span>
              </div>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: T.inkSoft }}>
                {authUser?.name || authUser?.email}
              </span>
              <button onClick={handleLogout} title="Sign out"
                style={{ background: "none", border: "none", cursor: "pointer",
                  color: T.muted, fontSize: 11.5, padding: "2px 6px", borderRadius: 6,
                  fontFamily: FONT_SANS, fontWeight: 600, letterSpacing: 0.2,
                  borderLeft: `1px solid ${T.line}`, marginLeft: 2 }}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: 64, color: T.muted, fontSize: 13.5 }}>
          <i className="ti ti-loader-2" style={{ fontSize: 26, display: "block", marginBottom: 10, animation: "fluid-spin 0.9s linear infinite", color: T.teal }} />
          Loading bugs from Slack channels via Asana…
        </div>
      )}

      {!loading && fetchError && (
        <ConnectionError apiUrl={API} errorDetail={fetchError} onRetry={handleRetry} retrying={retrying} />
      )}

      {!loading && !fetchError && (
        <>
          <div style={{ borderBottom: `1px solid ${T.line}`, display: "flex", marginBottom: 20, overflowX: "auto" }}>
            <Tab label="Dashboard"    active={tab === "dashboard"}     onClick={() => setTab("dashboard")} />
            <Tab label="All issues"   active={tab === "issues"}        onClick={() => setTab("issues")}    count={issues.length} />
            <Tab label="SME review"   active={tab === "sme"}           onClick={() => setTab("sme")}       count={unvalidatedCount} />
            <Tab label="SME dashboard" active={tab === "sme_dashboard"} onClick={() => setTab("sme_dashboard")} />
            <Tab label="Tracked"      active={tab === "tracked"}       onClick={() => setTab("tracked")}   count={issues.filter(i => i.tracking).length} />
            <Tab label="Google Sheet" active={tab === "sheet"}         onClick={() => setTab("sheet")} />
          </div>

          {tab === "dashboard"     && <Dashboard issues={issues} />}
          {tab === "sme_dashboard" && <SmeDashboard issues={issues} onViewIssue={setViewTarget} />}
          {tab === "sheet"         && <GoogleSheetView issues={issues} />}

          {(tab === "issues" || tab === "tracked" || tab === "sme") && (
            <div>
              {tab !== "sme" && (
                <div style={{ marginBottom: 16 }}>
                  {/* Row 1 — existing filters */}
                  <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                    {[
                      [filterChannel, setFilterChannel, "All channels", CHANNELS],
                      [filterType, setFilterType, "All types", ["bug", "feature"]],
                      [filterStatus, setFilterStatus, "All statuses", STATUSES],
                    ].map(([val, setter, allLabel, opts], idx) => (
                      <select key={idx} value={val} onChange={e => setter(e.target.value)} style={{
                        fontSize: 12.5, padding: "7px 11px", borderRadius: 8,
                        border: `1px solid ${T.line}`, backgroundColor: T.paperRaised,
                        color: T.inkSoft, fontFamily: FONT_SANS, fontWeight: 500,
                      }}>
                        <option value="all">{allLabel}</option>
                        {opts.map(o => <option key={o} value={o}>{o === "bug" ? "Bugs" : o === "feature" ? "Feature requests" : o}</option>)}
                      </select>
                    ))}
                    <button onClick={handleRetry} title="Refresh from Asana" style={{
                      fontSize: 12.5, padding: "7px 13px", borderRadius: 8,
                      border: `1px solid ${T.line}`, backgroundColor: T.paperRaised, cursor: "pointer",
                      color: T.inkSoft, display: "flex", alignItems: "center", gap: 6, fontWeight: 600, fontFamily: FONT_SANS,
                    }}>
                      <i className={`ti ${retrying ? "ti-loader-2" : "ti-refresh"}`} style={retrying ? { animation: "fluid-spin 0.8s linear infinite" } : {}} />
                      Refresh
                    </button>
                  </div>

                  {/* Row 2 — date range and agent filters */}
                  <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}>
                    {/* Date from */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6,
                      border: `1px solid ${filterDateFrom ? T.teal : T.line}`, borderRadius: 8,
                      backgroundColor: T.paperRaised, padding: "5px 11px" }}>
                      <i className="ti ti-calendar" style={{ fontSize: 14, color: filterDateFrom ? T.teal : T.mutedSoft }} />
                      <span style={{ fontSize: 11.5, color: T.mutedSoft, fontWeight: 600, whiteSpace: "nowrap" }}>From</span>
                      <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                        style={{ border: "none", outline: "none", fontSize: 12.5, color: T.inkSoft,
                          backgroundColor: "transparent", fontFamily: FONT_SANS, cursor: "pointer" }} />
                    </div>

                    {/* Date to */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6,
                      border: `1px solid ${filterDateTo ? T.teal : T.line}`, borderRadius: 8,
                      backgroundColor: T.paperRaised, padding: "5px 11px" }}>
                      <i className="ti ti-calendar" style={{ fontSize: 14, color: filterDateTo ? T.teal : T.mutedSoft }} />
                      <span style={{ fontSize: 11.5, color: T.mutedSoft, fontWeight: 600, whiteSpace: "nowrap" }}>To</span>
                      <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                        min={filterDateFrom || undefined}
                        style={{ border: "none", outline: "none", fontSize: 12.5, color: T.inkSoft,
                          backgroundColor: "transparent", fontFamily: FONT_SANS, cursor: "pointer" }} />
                    </div>

                    {/* Agent filter */}
                    <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)} style={{
                      fontSize: 12.5, padding: "7px 11px", borderRadius: 8,
                      border: `1px solid ${filterAgent !== "all" ? T.amber : T.line}`,
                      backgroundColor: filterAgent !== "all" ? T.amberBg : T.paperRaised,
                      color: filterAgent !== "all" ? T.amber : T.inkSoft,
                      fontFamily: FONT_SANS, fontWeight: filterAgent !== "all" ? 700 : 500,
                    }}>
                      <option value="all">All agents</option>
                      {agentOptions.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>

                    {/* Clear button — only shown when date or agent filter is active */}
                    {(filterDateFrom || filterDateTo || filterAgent !== "all") && (
                      <button onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); setFilterAgent("all"); }} style={{
                        fontSize: 12.5, padding: "7px 13px", borderRadius: 8,
                        border: `1px solid ${T.redLine}`, backgroundColor: T.redBg,
                        cursor: "pointer", color: T.red, display: "flex", alignItems: "center",
                        gap: 6, fontWeight: 600, fontFamily: FONT_SANS,
                      }}>
                        <i className="ti ti-x" style={{ fontSize: 13 }} />
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>
              )}

              {tab === "sme" && unvalidatedCount > 0 && (
                <div style={{
                  backgroundColor: T.amberBg, border: `1px solid ${T.amberLine}`, borderRadius: 11,
                  padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#8A3A07",
                  display: "flex", alignItems: "center", gap: 9, fontWeight: 500,
                }}>
                  <i className="ti ti-shield-check" style={{ fontSize: 16 }} />
                  <strong>{unvalidatedCount} issue{unvalidatedCount > 1 ? "s" : ""}</strong>&nbsp;awaiting SME validation.
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {currentList.map(issue => (
                  <IssueCard key={issue.id} issue={issue} onValidate={setValidateTarget} onView={setViewTarget} onDelete={handleDelete} />
                ))}
                {currentList.length === 0 && (
                  <div style={{
                    padding: 48, textAlign: "center", color: T.muted, fontSize: 13.5,
                    backgroundColor: T.paperRaised, border: `1px dashed ${T.lineStrong}`, borderRadius: 14,
                  }}>
                    <i className="ti ti-circle-check" style={{ fontSize: 28, display: "block", marginBottom: 10, color: T.greenLine }} />
                    {tab === "sme"
                      ? "All issues validated — nothing waiting on SME review."
                      : issues.length === 0
                        ? "No bugs synced from Slack yet. Post a message in #fluid-prodbugs to test the pipeline."
                        : "No issues match the current filters."}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
